import { EventName } from '@/shared/types/segment';

const KNOWN_EVENTS = new Set<string>(Object.values(EventName));

/** LogRocket's Segment integration sends this; it is not a product event. */
const ALLOWED_UNLISTED_EVENTS = new Set(['LogRocket']);

/** Longer than this is free text, not a dimension. */
const MAX_STRING_LENGTH = 80;

/** Written by analytics.js, not by a call site. `sanitizePayload` bounds these. */
const LIBRARY_KEYS = new Set(['url', 'path', 'referrer', 'search', 'title']);

type Payload = { type?: string; event?: string; properties?: Record<string, unknown> };

/**
 * Checks one payload against the contract in docs/analytics.md. Never mutates.
 *
 * @returns One message per problem; empty means the event follows the contract
 */
export const validateEvent = (payload: Payload): string[] => {
  const problems: string[] = [];
  const { type, event, properties = {} } = payload;

  const allowedUnlisted = Boolean(event && ALLOWED_UNLISTED_EVENTS.has(event));

  if (type === 'track' && event && !KNOWN_EVENTS.has(event) && !allowedUnlisted) {
    problems.push(`"${event}" is not in EventName.`);
  }

  // Only an event is expected to know which page produced it; `identify` and
  // `group` carry traits.
  if (type === 'track' && !allowedUnlisted && !properties.page_section) {
    problems.push('no page_section — the page tracked no page view.');
  }

  for (const [key, value] of Object.entries(properties)) {
    if (LIBRARY_KEYS.has(key)) {
      continue;
    }
    if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
      problems.push(
        `"${key}" is ${value.length} characters — send a length, an id or a bounded enum.`
      );
    }
  }

  return problems;
};

/**
 * Reports contract violations to the console, on the dev cluster and locally.
 * It only reads.
 *
 * @param analytics - The Segment client; a client with no middleware support is ignored
 */
export const registerEventValidation = (analytics: unknown) => {
  const host = analytics as {
    addSourceMiddleware?: (
      fn: (ctx: { payload: { obj: Payload }; next: (p: unknown) => void }) => void
    ) => unknown;
  };

  if (typeof host.addSourceMiddleware !== 'function') {
    return;
  }

  host.addSourceMiddleware(({ payload, next }) => {
    try {
      const problems = validateEvent(payload?.obj ?? {});
      if (problems.length > 0) {
        const name = payload?.obj?.event ?? payload?.obj?.type ?? 'event';
        console.error(`[analytics] ${name}:\n  - ${problems.join('\n  - ')}`);
      }
    } catch (error) {
      console.error('[analytics] the event validator itself failed', error);
    }
    next(payload);
  });
};
